package com.rainbowclubs.rainbow.presentation.admin

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.*
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import com.rainbowclubs.rainbow.data.models.Membership
import com.rainbowclubs.rainbow.data.models.MembershipStatus
import com.rainbowclubs.rainbow.data.models.UserRole
import com.rainbowclubs.rainbow.presentation.ScreenState
import com.rainbowclubs.rainbow.presentation.common.ErrorScreen
import com.rainbowclubs.rainbow.presentation.common.LoadingScreen
import com.rainbowclubs.rainbow.presentation.theme.RainbowClubTheme
import com.rainbowclubs.rainbow.presentation.theme.accentBlue
import com.rainbowclubs.rainbow.presentation.theme.transparent
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel
import org.koin.core.parameter.parametersOf
import rainbowclubs.composeapp.generated.resources.*

@Composable
fun PendingInvitationsScreen(
    clubId: String,
    viewModel: PendingInvitationsViewModel = koinViewModel(parameters = { parametersOf(clubId) }),
    navigateTo: (PendingInvitationsViewModel.NavigationEvent) -> Unit,
) {
    val screenState by viewModel.screenState.collectAsState()
    val actionLoadingState by viewModel.actionLoadingState.collectAsState()
    val showActionError by viewModel.showActionError.collectAsState()

    LaunchedEffect(Unit) {
        viewModel.navigationIntent.collect { intent ->
            navigateTo(intent)
        }
    }

    Scaffold(
        containerColor = MaterialTheme.colorScheme.transparent,
    ) { paddingValues ->
        Box(
            modifier = Modifier
                .padding(paddingValues)
                .fillMaxSize(),
        ) {
            when (val state = screenState) {
                is ScreenState.Loading -> LoadingScreen(Modifier.fillMaxSize())
                is ScreenState.Error -> ErrorScreen(
                    error = stringResource(Res.string.general_error),
                    onButtonClick = viewModel::fetchPendingInvitations,
                    buttonText = stringResource(Res.string.try_again_button),
                )

                is ScreenState.Content -> PendingInvitationsScreenContent(
                    invitations = state.data,
                    actionLoadingState = actionLoadingState,
                    showActionError = showActionError,
                    onBackClick = viewModel::onBackClick,
                    onAcceptClick = viewModel::acceptInvitation,
                    onDeclineClick = viewModel::declineInvitation,
                    onDismissActionError = viewModel::dismissActionError,
                )
            }
        }
    }
}

@Composable
private fun PendingInvitationsScreenContent(
    invitations: List<Membership>,
    actionLoadingState: Pair<String, MembershipStatus>?,
    showActionError: Boolean,
    onBackClick: () -> Unit,
    onAcceptClick: (String) -> Unit,
    onDeclineClick: (String) -> Unit,
    onDismissActionError: () -> Unit,
) {
    Column(
        modifier = Modifier
            .fillMaxSize()
            .verticalScroll(rememberScrollState()),
    ) {
        PendingInvitationsHeader(
            onBackClick = onBackClick,
        )

        Spacer(modifier = Modifier.height(22.dp))

        PendingInvitationsSummary(count = invitations.size)

        if (showActionError) {
            Spacer(modifier = Modifier.height(16.dp))
            ActionErrorBanner(onDismissClick = onDismissActionError)
        }

        Spacer(modifier = Modifier.height(24.dp))

        if (invitations.isEmpty()) {
            EmptyPendingInvitations()
        } else {
            Column(
                verticalArrangement = Arrangement.spacedBy(18.dp),
            ) {
                invitations.forEach { invitation ->
                    PendingInvitationCard(
                        invitation = invitation,
                        actionLoadingState = actionLoadingState,
                        onAcceptClick = onAcceptClick,
                        onDeclineClick = onDeclineClick,
                    )
                }
            }
        }

        Spacer(modifier = Modifier.height(36.dp))
    }
}

@Composable
private fun PendingInvitationsSummary(count: Int) {
    val accent = MaterialTheme.colorScheme.accentBlue
    Surface(
        shape = RoundedCornerShape(28.dp),
        color = accent.copy(alpha = 0.10f),
        contentColor = accent,
        border = BorderStroke(1.dp, accent.copy(alpha = 0.28f)),
    ) {
        Row(
            modifier = Modifier.padding(horizontal = 14.dp, vertical = 8.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Icon(
                painter = painterResource(Res.drawable.ic_invitation),
                contentDescription = stringResource(Res.string.new_invitations_icon_cd),
                modifier = Modifier.size(20.dp),
                tint = accent,
            )
            Spacer(modifier = Modifier.width(8.dp))
            Text(
                text = stringResource(Res.string.pending_invitations_count, count),
                style = MaterialTheme.typography.titleMedium,
            )
        }
    }
}

@Composable
private fun PendingInvitationsHeader(onBackClick: () -> Unit) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        TextButton(
            onClick = onBackClick,
            modifier = Modifier.size(40.dp),
            contentPadding = androidx.compose.foundation.layout.PaddingValues(0.dp),
        ) {
            Icon(
                painter = painterResource(Res.drawable.ic_arrow_back),
                contentDescription = stringResource(Res.string.back_icon_cd),
                modifier = Modifier.size(28.dp),
                tint = MaterialTheme.colorScheme.onBackground,
            )
        }

        Spacer(modifier = Modifier.width(20.dp))

        Column(modifier = Modifier.weight(1f)) {
            Text(
                text = stringResource(Res.string.new_invitations),
                color = MaterialTheme.colorScheme.onBackground,
                style = MaterialTheme.typography.titleLarge,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
            )

            Spacer(modifier = Modifier.height(8.dp))

            Text(
                text = stringResource(Res.string.pending_membership_requests),
                color = MaterialTheme.colorScheme.outline,
                style = MaterialTheme.typography.titleMedium,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
            )
        }
    }
}

@Composable
private fun ActionErrorBanner(onDismissClick: () -> Unit) {
    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(16.dp),
        color = MaterialTheme.colorScheme.errorContainer,
        contentColor = MaterialTheme.colorScheme.onErrorContainer,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.error.copy(alpha = 0.28f)),
    ) {
        Row(
            modifier = Modifier.padding(horizontal = 16.dp, vertical = 12.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Text(
                text = stringResource(Res.string.invitation_action_error),
                modifier = Modifier.weight(1f),
                style = MaterialTheme.typography.titleMedium,
            )
            TextButton(onClick = onDismissClick) {
                Text(
                    text = stringResource(Res.string.ok),
                    style = MaterialTheme.typography.titleMedium,
                )
            }
        }
    }
}

@Composable
private fun PendingInvitationCard(
    invitation: Membership,
    actionLoadingState: Pair<String, MembershipStatus>?,
    onAcceptClick: (String) -> Unit,
    onDeclineClick: (String) -> Unit,
) {
    val isActionLoading = actionLoadingState?.first == invitation.membershipId
    val loadingStatus = actionLoadingState?.second.takeIf { isActionLoading }
    val accent = MaterialTheme.colorScheme.accentBlue

    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(24.dp),
        color = MaterialTheme.colorScheme.surface,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outline.copy(alpha = 0.42f)),
    ) {
        Column(
            modifier = Modifier.padding(horizontal = 16.dp, vertical = 18.dp),
        ) {
            Row(
                verticalAlignment = Alignment.Top,
            ) {
                InvitationAvatar(invitation.displayName())
                Spacer(modifier = Modifier.width(16.dp))
                Column(modifier = Modifier.weight(1f)) {
                    Text(
                        text = invitation.displayName(),
                        color = MaterialTheme.colorScheme.onSurface,
                        style = MaterialTheme.typography.titleLarge,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis,
                    )
                    Spacer(modifier = Modifier.height(4.dp))
                    Text(
                        text = invitation.userId,
                        color = MaterialTheme.colorScheme.outline,
                        style = MaterialTheme.typography.titleMedium,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis,
                    )
                    Spacer(modifier = Modifier.height(8.dp))
                    RequestedDateRow(requestedAt = invitation.requestedAt)
                }
            }

            Spacer(modifier = Modifier.height(12.dp))

            MembershipDataPreview(membershipData = invitation.membershipData)

            Spacer(modifier = Modifier.height(16.dp))

            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(16.dp),
            ) {
                OutlinedButton(
                    onClick = { onAcceptClick(invitation.membershipId) },
                    modifier = Modifier.weight(1f),
                    enabled = !isActionLoading,
                    colors = ButtonDefaults.outlinedButtonColors(
                        containerColor = accent.copy(alpha = 0.08f),
                        contentColor = accent,
                    ),
                    border = BorderStroke(1.dp, accent.copy(alpha = 0.28f)),
                    shape = RoundedCornerShape(14.dp),
                ) {
                    if (loadingStatus == MembershipStatus.APPROVED) {
                        CircularProgressIndicator(
                            modifier = Modifier.size(18.dp),
                            strokeWidth = 2.dp,
                            color = accent,
                        )
                    } else {
                        Icon(
                            painter = painterResource(Res.drawable.ic_check),
                            contentDescription = null,
                            modifier = Modifier.size(18.dp),
                            tint = accent,
                        )
                        Spacer(modifier = Modifier.width(8.dp))
                        Text(
                            text = stringResource(Res.string.accept),
                            style = MaterialTheme.typography.titleMedium,
                        )
                    }
                }

                OutlinedButton(
                    onClick = { onDeclineClick(invitation.membershipId) },
                    modifier = Modifier.weight(1f),
                    enabled = !isActionLoading,
                    colors = ButtonDefaults.outlinedButtonColors(
                        containerColor = MaterialTheme.colorScheme.errorContainer.copy(alpha = 0.24f),
                        contentColor = MaterialTheme.colorScheme.error,
                    ),
                    border = BorderStroke(1.dp, MaterialTheme.colorScheme.error.copy(alpha = 0.22f)),
                    shape = RoundedCornerShape(14.dp),
                ) {
                    if (loadingStatus == MembershipStatus.BLOCKED) {
                        CircularProgressIndicator(
                            modifier = Modifier.size(18.dp),
                            strokeWidth = 2.dp,
                            color = MaterialTheme.colorScheme.error,
                        )
                    } else {
                        Icon(
                            painter = painterResource(Res.drawable.ic_cancel),
                            contentDescription = null,
                            modifier = Modifier.size(18.dp),
                            tint = MaterialTheme.colorScheme.error,
                        )
                        Spacer(modifier = Modifier.width(8.dp))
                        Text(
                            text = stringResource(Res.string.decline),
                            style = MaterialTheme.typography.titleMedium,
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun RequestedDateRow(requestedAt: String?) {
    Row(
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Icon(
            painter = painterResource(Res.drawable.ic_calendar),
            contentDescription = null,
            modifier = Modifier.size(18.dp),
            tint = MaterialTheme.colorScheme.outline,
        )
        Spacer(modifier = Modifier.width(8.dp))
        Text(
            text = stringResource(
                Res.string.requested_at_value,
                requestedAt.requestedDateLabel(),
            ),
            color = MaterialTheme.colorScheme.outline,
            style = MaterialTheme.typography.titleMedium,
            maxLines = 1,
            overflow = TextOverflow.Ellipsis,
        )
    }
}

@Composable
private fun InvitationAvatar(displayName: String) {
    val accent = MaterialTheme.colorScheme.accentBlue
    Box(
        modifier = Modifier
            .size(54.dp)
            .background(accent.copy(alpha = 0.12f), CircleShape),
        contentAlignment = Alignment.Center,
    ) {
        Text(
            text = displayName.initials(),
            color = accent,
            style = MaterialTheme.typography.titleLarge,
            maxLines = 1,
        )
    }
}

@Composable
private fun MembershipDataPreview(membershipData: Map<String, String>) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .background(MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.36f), RoundedCornerShape(14.dp))
            .padding(horizontal = 14.dp, vertical = 12.dp),
        verticalArrangement = Arrangement.spacedBy(6.dp),
    ) {
        if (membershipData.isEmpty()) {
            Text(
                text = stringResource(Res.string.no_membership_data_required),
                color = MaterialTheme.colorScheme.outline,
                style = MaterialTheme.typography.titleMedium,
            )
        } else {
            membershipData.entries.forEach { (label, value) ->
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                ) {
                    Text(
                        text = label,
                        modifier = Modifier.widthIn(max = 128.dp),
                        color = MaterialTheme.colorScheme.outline,
                        style = MaterialTheme.typography.titleMedium,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis,
                    )
                    Text(
                        text = value,
                        modifier = Modifier.weight(1f),
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                        style = MaterialTheme.typography.titleMedium,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis,
                    )
                }
            }
        }
    }
}

@Composable
private fun EmptyPendingInvitations() {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .padding(top = 92.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        EmptyInvitationsIcon()

        Spacer(modifier = Modifier.height(24.dp))

        Text(
            text = stringResource(Res.string.no_pending_invitations),
            color = MaterialTheme.colorScheme.onSurface,
            style = MaterialTheme.typography.titleLarge,
        )

        Spacer(modifier = Modifier.height(8.dp))

        Text(
            text = stringResource(Res.string.no_pending_invitations_description),
            color = MaterialTheme.colorScheme.outline,
            style = MaterialTheme.typography.titleMedium,
        )
    }
}

@Composable
private fun EmptyInvitationsIcon() {
    val accent = MaterialTheme.colorScheme.accentBlue

    Box(
        modifier = Modifier
            .width(128.dp)
            .height(104.dp),
        contentAlignment = Alignment.Center,
    ) {
        Box(
            modifier = Modifier
                .align(Alignment.TopStart)
                .padding(start = 20.dp, top = 24.dp)
                .size(6.dp)
                .background(MaterialTheme.colorScheme.error.copy(alpha = 0.72f), CircleShape),
        )

        Box(
            modifier = Modifier
                .align(Alignment.TopEnd)
                .padding(end = 24.dp, top = 38.dp)
                .size(7.dp)
                .background(accent.copy(alpha = 0.72f), CircleShape),
        )

        Surface(
            modifier = Modifier
                .align(Alignment.BottomCenter)
                .width(96.dp)
                .height(58.dp),
            shape = RoundedCornerShape(12.dp),
            color = accent.copy(alpha = 0.12f),
            border = BorderStroke(1.dp, accent.copy(alpha = 0.34f)),
        ) {
            Box(
                modifier = Modifier.fillMaxSize(),
                contentAlignment = Alignment.TopCenter,
            ) {
                Surface(
                    modifier = Modifier
                        .padding(top = 1.dp)
                        .width(42.dp)
                        .height(16.dp),
                    shape = RoundedCornerShape(bottomStart = 10.dp, bottomEnd = 10.dp),
                    color = MaterialTheme.colorScheme.surface,
                    border = BorderStroke(1.dp, accent.copy(alpha = 0.28f)),
                ) {}
            }
        }

        Surface(
            modifier = Modifier
                .align(Alignment.TopCenter)
                .size(54.dp),
            shape = CircleShape,
            color = MaterialTheme.colorScheme.surface,
            border = BorderStroke(2.dp, accent.copy(alpha = 0.36f)),
        ) {
            Box(contentAlignment = Alignment.Center) {
                Icon(
                    painter = painterResource(Res.drawable.ic_check),
                    contentDescription = null,
                    modifier = Modifier.size(34.dp),
                    tint = accent,
                )
            }
        }
    }
}

private fun Membership.displayName(): String {
    return userDisplayName.ifBlank { userId }
}

private fun String.initials(): String {
    val parts = trim().split(" ").filter { it.isNotBlank() }
    return parts.take(2).joinToString("") { it.first().uppercase() }.ifBlank { "?" }
}

@Preview(name = "Pending Invitations - List")
@Composable
private fun PendingInvitationsListPreview() {
    RainbowClubTheme {
        Surface {
            PendingInvitationsScreenContent(
                invitations = samplePendingInvitations(),
                actionLoadingState = null,
                showActionError = false,
                onBackClick = {},
                onAcceptClick = {},
                onDeclineClick = {},
                onDismissActionError = {},
            )
        }
    }
}

@Preview(name = "Pending Invitations - Empty")
@Composable
private fun PendingInvitationsEmptyPreview() {
    RainbowClubTheme {
        Surface {
            PendingInvitationsScreenContent(
                invitations = emptyList(),
                actionLoadingState = null,
                showActionError = false,
                onBackClick = {},
                onAcceptClick = {},
                onDeclineClick = {},
                onDismissActionError = {},
            )
        }
    }
}

private fun samplePendingInvitations(): List<Membership> {
    return listOf(
        Membership(
            userDisplayName = "Alex Morgan",
            userId = "alex@example.com",
            clubName = "Rainbow Club",
            clubId = "club-1",
            membershipId = "membership-1",
            role = UserRole.MEMBER,
            status = MembershipStatus.PENDING,
            membershipData = mapOf(
                "Age group" to "Adult",
                "Pronouns" to "They/them",
            ),
            requestedAt = "2026-06-15T09:30:00Z",
        ),
        Membership(
            userDisplayName = "Sam Rivera",
            userId = "sam@example.com",
            clubName = "Rainbow Club",
            clubId = "club-1",
            membershipId = "membership-2",
            role = UserRole.MEMBER,
            status = MembershipStatus.PENDING,
            membershipData = mapOf("Volunteer" to "Yes"),
            requestedAt = "2026-06-14T17:10:00Z",
        ),
    )
}

private fun String?.requestedDateLabel(): String {
    val value = this?.trim().orEmpty()
    if (value.isBlank()) return "-"

    return value.substringBefore("T")
}
